import type { SiteId } from '@beaded/models'

import * as apiUtils from '.'

export interface ForecastRequestStatus {
  forecastingEnabled: boolean
  alreadyRequested: boolean
}

export const requestForecast = (siteId: SiteId) =>
  apiUtils.POST('/user/forecast-request', { siteId })

// Withdraws the site's open request. Idempotent server-side: cancelled is
// false when there was nothing open to cancel.
export const cancelForecast = (
  siteId: SiteId,
): Promise<{ cancelled: boolean }> =>
  apiUtils.DELETE(`/user/forecast-request/${siteId}`)

export const getForecastStatus = (
  siteId: SiteId,
  options?: Parameters<typeof apiUtils.GET>[1],
): Promise<ForecastRequestStatus> =>
  apiUtils.GET(`/user/forecast-request/${siteId}`, options)

// Model a site may be assigned to, per the #4169 D5 compatibility rule
// (packages/api-lib/src/forecastModelCompatibility.ts is the single source of
// truth on the server). `modelType` here is a ModelFactory model SLUG (e.g.
// `fleet-temperature-forecaster`), not the router's `model_type` argument --
// see that file's header before using this value in UI copy.
export interface CompatibleForecastModel {
  modelType: string
  label: string
}

// Hand-written mirror of the Zod output schema of
// GET /api/v1/admin/forecast-requests; the generated OpenAPI spec is the
// contract source of truth if they ever disagree.
export interface ForecastRequestAdminRow {
  id: string
  siteId: number
  siteName: string | null
  projectId: number | null
  projectName: string | null
  organizationId: number | null
  organizationName: string | null
  userId: string | null
  userNameFirst: string | null
  userNameLast: string | null
  userEmail: string | null
  status: string
  requestedAt: string
  // The model chosen at THIS approval (an immutable audit record); null while
  // the request is still open.
  modelType: string | null
  approvedAt: string | null
  // Null for the D10 grandfathered sites -- routed during ModelFactory
  // bring-up, never approved by a human. Never synthesize a name for these.
  approvedByName: string | null
  // The site's CURRENT assignment (legacy_site_.forecast_model_type) -- what
  // the nightly job will actually act on. May differ from `modelType` above if
  // the site was re-approved onto a different model since this request.
  siteForecastModelType: string | null
  // Models this site may be assigned to right now. Empty is a legitimate
  // answer -- render `incompatibleReason` in its place, never an empty
  // dropdown.
  compatibleModels: CompatibleForecastModel[]
  incompatibleReason: string | null
}

export interface ForecastRequestAdminReport {
  requests: ForecastRequestAdminRow[]
  // False when ModelFactory's active-cable roster could not be read, so the
  // UI can say North Slope eligibility is unknown rather than silently absent.
  tundraRosterAvailable: boolean
  // #3844: total count across ALL non-deleted requests, not just this page.
  total: number
}

export interface ForecastRequestAdminReportParams {
  limit: number
  offset: number
}

export const getAdminReport = (
  params: ForecastRequestAdminReportParams,
): Promise<ForecastRequestAdminReport> =>
  apiUtils.GET('/admin/forecast-requests', { params })

export interface ForecastRequestApproveResult {
  id: string
  siteId: number
  status: string
  modelType: string | null
  approvedAt: string | null
}

// Approving ENROLS the site in production forecasting on `modelType` -- it
// writes NO visibility field. Display stays governed by the separate
// show_forecast toggle until #4172 is decided (spec D7/D9).
export const approve = (
  id: string,
  modelType: string,
): Promise<ForecastRequestApproveResult> =>
  apiUtils.POST(`/admin/forecast-requests/${id}/approve`, { modelType })

export interface ForecastRequestDeclineResult {
  id: string
  siteId: number
  status: string
}

export const decline = (id: string): Promise<ForecastRequestDeclineResult> =>
  apiUtils.POST(`/admin/forecast-requests/${id}/decline`, {})
